import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import FixedSidebar from '../../components/ui/FixedSidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import authService from '../../services/authService';
import jobService from '../../services/jobService';
import { hasActionAccess } from '../../utils/roleBasedAccess';

// Import form sections
import BasicInformationSection from '../../components/job-management/BasicInformationSection';
import CompensationSection from '../../components/job-management/CompensationSection';
import JobDescriptionSection from '../../components/job-management/JobDescriptionSection';
import RequirementsSection from '../../components/job-management/RequirementsSection';
import SkillsSection from '../../components/job-management/SkillsSection';
import AdditionalInformationSection from '../../components/job-management/AdditionalInformationSection';
import BenefitsCertificationsSection from '../../components/job-management/BenefitsCertificationsSection';
import InterviewProcessSection from '../../components/job-management/InterviewProcessSection';
import PublicationStatusSection from '../../components/job-management/PublicationStatusSection';

const CreateJobPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(authService.getStoredUser());
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [success, setSuccess] = useState(false);

  // Form state
  const [jobData, setJobData] = useState({
    title: '',
    department: '',
    location: '',
    employment_type: 'full-time',
    experience_level: 'mid',
    salary_range_min: '',
    salary_range_max: '',
    currency: 'MWK',
    description: '',
    requirements: [],
    responsibilities: [],
    skills_required: [],
    skills_preferred: [],
    benefits: [],
    remote_work_allowed: false,
    travel_required: 'none',
    education_required: 'bachelor',
    certifications_required: [],
    application_deadline: '',
    start_date: '',
    reporting_manager: '',
    team_size: '',
    interview_process: [],
    status: 'draft',
    priority: 'medium'
  });

  // Arrays for dynamic form sections
  const [newRequirement, setNewRequirement] = useState('');
  const [newResponsibility, setNewResponsibility] = useState('');
  const [newSkillRequired, setNewSkillRequired] = useState('');
  const [newSkillPreferred, setNewSkillPreferred] = useState('');
  const [newBenefit, setNewBenefit] = useState('');
  const [newCertification, setNewCertification] = useState('');
  const [newInterviewStep, setNewInterviewStep] = useState('');

  // Check permissions
  const canCreateJobs = hasActionAccess(currentUser?.role, 'jobs', 'create-jobs');

  if (!canCreateJobs) {
    return (
      <div className="min-h-screen bg-background">
        <Header currentUser={currentUser} />
        <FixedSidebar currentPath="/job-management/create" />
        
        <main className="ml-72 pt-16">
          <div className="p-6">
            <div className="text-center py-12">
              <Icon name="Lock" size={48} className="mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium text-foreground mb-2">Access Denied</h3>
              <p className="text-muted-foreground mb-4">
                You don't have permission to create job postings.
              </p>
              <Button onClick={() => navigate('/hr-dashboard')} variant="outline">
                Return to Dashboard
              </Button>
            </div>
          </div>
        </main>
      </div>
    );
  }

  const resetForm = () => {
    setJobData({
      title: '',
      department: '',
      location: '',
      employment_type: 'full-time',
      experience_level: 'mid',
      salary_range_min: '',
      salary_range_max: '',
      currency: 'MWK',
      description: '',
      requirements: [],
      responsibilities: [],
      skills_required: [],
      skills_preferred: [],
      benefits: [],
      remote_work_allowed: false,
      travel_required: 'none',
      education_required: 'bachelor',
      certifications_required: [],
      application_deadline: '',
      start_date: '',
      reporting_manager: '',
      team_size: '',
      interview_process: [],
      status: 'draft',
      priority: 'medium'
    });
    
    // Reset array input states
    setNewRequirement('');
    setNewResponsibility('');
    setNewSkillRequired('');
    setNewSkillPreferred('');
    setNewBenefit('');
    setNewCertification('');
    setNewInterviewStep('');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setJobData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const addToArray = useCallback((arrayName, value, setValue) => {
    if (value.trim()) {
      setJobData(prev => ({
        ...prev,
        [arrayName]: [...prev[arrayName], value.trim()]
      }));
      setValue('');
    }
  }, []);

  const removeFromArray = useCallback((arrayName, index) => {
    setJobData(prev => ({
      ...prev,
      [arrayName]: prev[arrayName].filter((_, i) => i !== index)
    }));
  }, []);

  const validateForm = () => {
    // Use job service validation
    const validation = jobService.validateJobData(jobData);
    
    if (!validation.isValid) {
      setErrors(validation.errors);
      return false;
    }
    
    // Clear errors if validation passes
    setErrors({});
    
    // Log warnings if any
    if (validation.warnings.length > 0) {
      console.warn('Job creation warnings:', validation.warnings);
    }
    
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({}); // Clear previous errors
    setSuccess(false); // Clear previous success
    
    try {
      // Prepare payload with proper data types
      const payload = {
        ...jobData,
        salary_range_min: jobData.salary_range_min ? parseInt(jobData.salary_range_min) : null,
        salary_range_max: jobData.salary_range_max ? parseInt(jobData.salary_range_max) : null,
        team_size: jobData.team_size ? parseInt(jobData.team_size) : null,
        application_deadline: jobData.application_deadline ? new Date(jobData.application_deadline).toISOString() : null,
        start_date: jobData.start_date ? new Date(jobData.start_date).toISOString() : null,
      };

      console.log('Submitting job data:', payload);
      
      const result = await jobService.createJob(payload);
      
      console.log('Job created successfully:', result);
      
      // Show success message and reset form
      setSuccess(true);
      resetForm();
      
      // Scroll to top to show success message
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      // Auto-hide success message after 5 seconds and optionally navigate
      setTimeout(() => {
        setSuccess(false);
        // Uncomment if you want to navigate back after success
         navigate('/job-management', { 
          state: { 
             message: 'Job created successfully!', 
           type: 'success' 
           } 
         });
      }, 5000);
      
    } catch (error) {
      console.error('Error creating job:', error);
      
      // Enhanced error handling
      let errorMessage = 'Failed to create job. Please try again.';
      
      if (error.message) {
        errorMessage = error.message;
      } else if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.status) {
        switch (error.response.status) {
          case 401:
            errorMessage = 'You are not authorized to create jobs. Please log in again.';
            break;
          case 403:
            errorMessage = 'You do not have permission to create jobs.';
            break;
          case 422:
            errorMessage = 'Invalid job data. Please check your inputs.';
            break;
          case 500:
            errorMessage = 'Server error. Please try again later.';
            break;
          default:
            errorMessage = `Request failed with status ${error.response.status}`;
        }
      }
      
      setErrors({ submit: errorMessage });
      
      // Scroll to error message
      setTimeout(() => {
        const errorElement = document.querySelector('[data-error="submit"]');
        if (errorElement) {
          errorElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
      
    } finally {
      setLoading(false);
    }
  };

  // Create stable handlers for ArrayInput components
  const handleRequirementAdd = useCallback(() => {
    addToArray('requirements', newRequirement, setNewRequirement);
  }, [addToArray, newRequirement]);

  const handleRequirementRemove = useCallback((index) => {
    removeFromArray('requirements', index);
  }, [removeFromArray]);

  const handleResponsibilityAdd = useCallback(() => {
    addToArray('responsibilities', newResponsibility, setNewResponsibility);
  }, [addToArray, newResponsibility]);

  const handleResponsibilityRemove = useCallback((index) => {
    removeFromArray('responsibilities', index);
  }, [removeFromArray]);

  const handleSkillRequiredAdd = useCallback(() => {
    addToArray('skills_required', newSkillRequired, setNewSkillRequired);
  }, [addToArray, newSkillRequired]);

  const handleSkillRequiredRemove = useCallback((index) => {
    removeFromArray('skills_required', index);
  }, [removeFromArray]);

  const handleSkillPreferredAdd = useCallback(() => {
    addToArray('skills_preferred', newSkillPreferred, setNewSkillPreferred);
  }, [addToArray, newSkillPreferred]);

  const handleSkillPreferredRemove = useCallback((index) => {
    removeFromArray('skills_preferred', index);
  }, [removeFromArray]);

  const handleBenefitAdd = useCallback(() => {
    addToArray('benefits', newBenefit, setNewBenefit);
  }, [addToArray, newBenefit]);

  const handleBenefitRemove = useCallback((index) => {
    removeFromArray('benefits', index);
  }, [removeFromArray]);

  const handleCertificationAdd = useCallback(() => {
    addToArray('certifications_required', newCertification, setNewCertification);
  }, [addToArray, newCertification]);

  const handleCertificationRemove = useCallback((index) => {
    removeFromArray('certifications_required', index);
  }, [removeFromArray]);

  const handleInterviewStepAdd = useCallback(() => {
    addToArray('interview_process', newInterviewStep, setNewInterviewStep);
  }, [addToArray, newInterviewStep]);

  const handleInterviewStepRemove = useCallback((index) => {
    removeFromArray('interview_process', index);
  }, [removeFromArray]);

  return (
    <div className="min-h-screen bg-background">
      <Header currentUser={currentUser} />
      <FixedSidebar currentPath="/job-management/create" />
      
      <main className="ml-72 pt-16">
        <div className="p-6 max-w-4xl">
          <Breadcrumb customBreadcrumbs={[
            { label: 'Job Management', href: '/job-management' },
            { label: 'Create Job', href: '/job-management/create', isActive: true }
          ]} />

          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-3xl font-bold text-foreground mb-2">Create Job Posting</h1>
              <p className="text-muted-foreground">
                Create a new job posting with detailed parameters and requirements
              </p>
            </div>
            <Button variant="outline" onClick={() => navigate('/job-management')}>
              <Icon name="ArrowLeft" size={16} className="mr-2" />
              Back to Jobs
            </Button>
          </div>

          {/* Success Message */}
          {success && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={20} className="text-green-600 mr-3" />
                <div>
                  <h3 className="text-green-800 font-medium">Job Created Successfully!</h3>
                  <p className="text-green-700 text-sm mt-1">
                    Your job posting has been created. You can view it in the job management section.
                  </p>
                </div>
                <div className="flex space-x-2 ml-auto">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setSuccess(false)}
                    className="text-green-600 border-green-300 hover:bg-green-50"
                  >
                    Create Another
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => navigate('/job-management')}
                    className="text-green-600 hover:text-green-700"
                  >
                    View Jobs
                    <Icon name="ArrowRight" size={16} className="ml-1" />
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Basic Information */}
            <BasicInformationSection 
              jobData={jobData} 
              handleInputChange={handleInputChange} 
              errors={errors} 
            />

            {/* Salary Information */}
            <CompensationSection 
              jobData={jobData} 
              handleInputChange={handleInputChange} 
              errors={errors} 
            />

            {/* Job Description */}
            <JobDescriptionSection 
              jobData={jobData} 
              handleInputChange={handleInputChange} 
              errors={errors} 
            />

            {/* Requirements and Responsibilities */}
            <RequirementsSection
              jobData={jobData}
              newRequirement={newRequirement}
              setNewRequirement={setNewRequirement}
              newResponsibility={newResponsibility}
              setNewResponsibility={setNewResponsibility}
              handleRequirementAdd={handleRequirementAdd}
              handleRequirementRemove={handleRequirementRemove}
              handleResponsibilityAdd={handleResponsibilityAdd}
              handleResponsibilityRemove={handleResponsibilityRemove}
            />

            {/* Skills */}
            <SkillsSection
              jobData={jobData}
              newSkillRequired={newSkillRequired}
              setNewSkillRequired={setNewSkillRequired}
              newSkillPreferred={newSkillPreferred}
              setNewSkillPreferred={setNewSkillPreferred}
              handleSkillRequiredAdd={handleSkillRequiredAdd}
              handleSkillRequiredRemove={handleSkillRequiredRemove}
              handleSkillPreferredAdd={handleSkillPreferredAdd}
              handleSkillPreferredRemove={handleSkillPreferredRemove}
            />

            {/* Additional Information */}
            <AdditionalInformationSection 
              jobData={jobData} 
              handleInputChange={handleInputChange} 
            />

            {/* Benefits and Certifications */}
            <BenefitsCertificationsSection
              jobData={jobData}
              newBenefit={newBenefit}
              setNewBenefit={setNewBenefit}
              newCertification={newCertification}
              setNewCertification={setNewCertification}
              handleBenefitAdd={handleBenefitAdd}
              handleBenefitRemove={handleBenefitRemove}
              handleCertificationAdd={handleCertificationAdd}
              handleCertificationRemove={handleCertificationRemove}
            />

            {/* Interview Process */}
            <InterviewProcessSection
              jobData={jobData}
              newInterviewStep={newInterviewStep}
              setNewInterviewStep={setNewInterviewStep}
              handleInterviewStepAdd={handleInterviewStepAdd}
              handleInterviewStepRemove={handleInterviewStepRemove}
            />

            {/* Status */}
            <PublicationStatusSection 
              jobData={jobData} 
              handleInputChange={handleInputChange} 
            />

            {/* Form Actions */}
            <div className="flex items-center justify-between pt-6 border-t border-border">
              <Button
                type="button"
                variant="outline"
                onClick={() => navigate('/job-management')}
              >
                Cancel
              </Button>

              <div className="flex space-x-3">
                <Button
                  type="submit"
                  disabled={loading}
                  className="min-w-[120px]"
                >
                  {loading ? (
                    <>
                      <Icon name="Loader2" size={16} className="mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Icon name="Plus" size={16} className="mr-2" />
                      Create Job
                    </>
                  )}
                </Button>
              </div>
            </div>

            {errors.submit && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mt-4" data-error="submit">
                <div className="flex items-center">
                  <Icon name="AlertCircle" size={20} className="text-red-600 mr-3" />
                  <div>
                    <h3 className="text-red-800 font-medium">Error Creating Job</h3>
                    <p className="text-red-700 text-sm mt-1">{errors.submit}</p>
                  </div>
                </div>
              </div>
            )}
          </form>
        </div>
      </main>
    </div>
  );
};

export default CreateJobPage;